import { motion } from 'framer-motion';
import { useEffect, useState, useRef } from 'react';

const codeSnippets = [
  `// React Component
import { useState, useEffect } from 'react';

const EnterpriseApp = () => {
  const [data, setData] = useState(null);
  
  useEffect(() => {
    async function init() {
      const res = await connectToCore();
      setData(res.stream);
    }
    init();
  }, []);
  
  return <Architecture stream={data} />;
};`,

  `// Node.js API Service
const express = require('express');
const cluster = require('cluster');

if (cluster.isMaster) {
  for (let i = 0; i < require('os').cpus().length; i++) {
    cluster.fork();
  }
} else {
  const app = express();
  app.post('/api/v1/deploy', (req, res) => {
    res.status(200).json({ status: 'active', node: process.pid });
  });
  app.listen(8080);
}`,

  `# Python Data Processing
import pandas as pd
import numpy as np
from tensorflow import keras

def train_model(dataset):
    df = pd.read_csv(dataset)
    X = df.drop('target', axis=1)
    y = df['target']
    
    model = keras.Sequential([
        keras.layers.Dense(64, activation='relu'),
        keras.layers.Dense(1, activation='sigmoid')
    ])
    model.compile(optimizer='adam', loss='binary_crossentropy')
    return model.fit(X, y, epochs=100)`,

  `<?php
// PHP Core Interface
namespace App\\Core;

interface ArchitectureInterface {
    public function deploy(string $env): bool;
    public function scale(int $instances): void;
}

class SystemManager implements ArchitectureInterface {
    public function deploy(string $env): bool {
        return Cache::remember('deploy_status', 60, function() {
            return true;
        });
    }
}`,

  `-- MySQL Schema
CREATE TABLE system_metrics (
    id UUID PRIMARY KEY,
    node_name VARCHAR(255) NOT NULL,
    cpu_usage DECIMAL(5,2),
    memory_alloc BIGINT,
    recorded_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

CREATE INDEX idx_metrics_time 
ON system_metrics (recorded_at DESC);`
];

const TypewriterSnippet = ({ snippet }) => {
  const textRef = useRef(null);

  useEffect(() => {
    let timeout;
    let isTyping = true;
    let textLength = 0;

    const tick = () => {
      if (!textRef.current) return;

      if (isTyping) {
        if (textLength < snippet.text.length) {
          textLength++;
          textRef.current.textContent = snippet.text.slice(0, textLength) + "_";
          timeout = setTimeout(tick, 10 + Math.random() * 20);
        } else {
          textRef.current.textContent = snippet.text;
          isTyping = false;
          timeout = setTimeout(tick, 8000 + Math.random() * 5000);
        }
      } else {
        if (textLength > 0) {
          textLength--;
          textRef.current.textContent = snippet.text.slice(0, textLength) + "_";
          timeout = setTimeout(tick, 2 + Math.random() * 8);
        } else {
          textRef.current.textContent = "_";
          isTyping = true;
          timeout = setTimeout(tick, 2000 + Math.random() * 3000);
        }
      }
    };

    timeout = setTimeout(tick, snippet.delay || 100);
    return () => clearTimeout(timeout);
  }, [snippet.text, snippet.delay]);

  return (
    <div
      ref={textRef}
      className="absolute font-mono font-bold selection:bg-transparent text-sm sm:text-base whitespace-pre drop-shadow-md z-0"
      style={{
        color: 'var(--color-primary, #10b981)',
        top: snippet.top,
        left: snippet.left,
        opacity: snippet.opacity,
        transform: `scale(${snippet.scale})`,
        pointerEvents: 'none'
      }}
    />
  );
};

const CodingBackground = ({ positions, opacityRange = [0.10, 0.20], maxSnippets }) => {
  const [snippets, setSnippets] = useState([]);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;

    // Fallback default positions if none provided via props
    const defaultPositions = [
      { top: '5%', left: '5%' },
      { top: '40%', left: '60%' },
      { top: '65%', left: '5%' },
      { top: '15%', left: '55%' }
    ];

    const targetPositions = positions || defaultPositions;
    const numSnippets = maxSnippets !== undefined ? maxSnippets : (isMobile ? 1 : Math.min(2, targetPositions.length));

    const generated = Array.from({ length: numSnippets }).map((_, i) => ({
      id: i,
      text: codeSnippets[Math.floor(Math.random() * codeSnippets.length)],
      top: targetPositions[i % targetPositions.length].top,
      left: targetPositions[i % targetPositions.length].left,
      opacity: opacityRange[0] + Math.random() * (opacityRange[1] - opacityRange[0]),
      scale: 0.8 + Math.random() * 0.3,
      delay: Math.random() * 2000
    }));

    setSnippets(generated);
  }, [positions, JSON.stringify(opacityRange), maxSnippets]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {snippets.map(snippet => (
        <TypewriterSnippet key={snippet.id} snippet={snippet} />
      ))}
    </div>
  );
};

export default CodingBackground;
